import { useEffect, useState } from "react";
import { api, ErrorApi } from "../api.ts";
import { dinero } from "../../../dominio/src/index.ts";
import { AvisoError, useAccion } from "./comun.tsx";

interface Ficha {
  id: string; hc: number; nombres: string; apellidos: string; doc: string; nacimiento: string | null; telefono: string; registro_rapido: boolean; es_prueba: boolean; creado: string;
  registros: { citas?: number; notas?: number; abonos?: number; tratamientos?: number; fotos?: number };
}
interface Pareja { a: Ficha; b: Ficha }

const fecha = (iso: string | null) => (iso ? iso.split("-").reverse().join("/") : "—");
const dia = (iso: string) => iso.slice(0, 10).split("-").reverse().join("/");

function resumen(r: Ficha["registros"]) {
  const partes = [[r.citas, "cita"], [r.notas, "nota"], [r.abonos, "abono"], [r.tratamientos, "tratamiento"], [r.fotos, "foto"]] as [number | undefined, string][];
  const con = partes.filter(([n]) => n).map(([n, t]) => `${n} ${t}${n === 1 ? "" : "s"}`);
  return con.length ? con.join(" · ") : "sin registros";
}

/**
 * Configuración → «Pacientes duplicados» (administración): las fichas que parecen ser la misma persona. Se elige cuál queda como principal y la otra se
 * ARCHIVA: no se borra ni se mueve nada firmado (sigue consultable desde la ficha principal) y solo las citas futuras pasan a la principal.
 */
export function DuplicadosPacientes() {
  const [datos, setDatos] = useState<{ total: number; parejas: Pareja[] } | null>(null);
  const [msg, setMsg] = useState("");
  const a = useAccion();
  const cargar = () => api<{ total: number; parejas: Pareja[] }>("GET", "/api/duplicados-pacientes").then(setDatos).catch(a.setError);
  useEffect(() => { cargar(); }, []);

  async function archivar(sobrante: Ficha, principal: Ficha) {
    const motivo = prompt(`Se archivará la ficha HC ${sobrante.hc} (${sobrante.nombres} ${sobrante.apellidos}) y quedará como principal la HC ${principal.hc}.\n\nNo se borra nada: sus registros se siguen viendo desde la ficha principal. Escribe el motivo:`, "Registrado dos veces");
    if (motivo === null) return;
    let confirmar = false;
    for (;;) {
      try {
        const r = await api<{ citasMovidas: number }>("POST", `/api/pacientes/${sobrante.id}/archivar`, { principalId: principal.id, motivo, ...(confirmar ? { confirmarPendientes: true } : {}) });
        setMsg(`Listo: la ficha HC ${sobrante.hc} quedó archivada.${r.citasMovidas ? ` ${r.citasMovidas === 1 ? "Su cita futura pasó" : `Sus ${r.citasMovidas} citas futuras pasaron`} a la ficha principal.` : ""}`);
        a.setError(null);
        await cargar();
        return;
      } catch (e) {
        if (e instanceof ErrorApi && e.datos?.codigo === "pendientes_en_duplicado") {
          const d = e.datos as { saldo?: number; porHacer?: number; membresias?: number };
          const cosas = [d.saldo ? `un saldo por cobrar de ${dinero(d.saldo)}` : "", d.porHacer ? `${d.porHacer} tratamiento${d.porHacer === 1 ? "" : "s"} por hacer` : "", d.membresias ? `${d.membresias} membresía${d.membresias === 1 ? "" : "s"} vigente${d.membresias === 1 ? "" : "s"} del club` : ""].filter(Boolean).join(", ");
          if (!confirm(`La ficha HC ${sobrante.hc} tiene ${cosas}.\n\nSi la archivas, eso NO pasa a la ficha principal: seguirá visible en la sección «Fichas anteriores» de la ficha principal, y el saldo seguirá contando en los reportes.\n\n¿Archivarla igual?`)) return;
          confirmar = true;
          continue;
        }
        a.setError(e instanceof Error ? e : new Error("No se pudo archivar la ficha."));
        return;
      }
    }
  }

  async function noSonIguales(p: Pareja) {
    if (!confirm("¿Confirmas que son dos personas distintas? Esta pareja dejará de aparecer en la lista.")) return;
    const r = await a.correr(() => api("POST", "/api/duplicados-pacientes/descartar", { a: p.a.id, b: p.b.id }));
    if (r) { setMsg("Listo: esa pareja ya no aparecerá."); await cargar(); }
  }

  const lado = (f: Ficha, otra: Ficha) => (
    <div className="card" style={{ boxShadow: "none", display: "grid", gap: 4, alignContent: "start" }}>
      <b>{f.nombres} {f.apellidos}</b>
      <span className="muted mono">HC {f.hc}{f.doc ? ` · ${f.doc}` : " · sin cédula"}</span>
      <span>Nacimiento: {fecha(f.nacimiento)} · Celular: {f.telefono || "—"}</span>
      <span className="muted">Creada el {dia(f.creado)}{f.registro_rapido ? " · registro rápido" : ""}{f.es_prueba ? " · de prueba" : ""}</span>
      <span className="muted">{resumen(f.registros)}</span>
      <div className="fila" style={{ flexWrap: "wrap", gap: 6, marginTop: 4 }}>
        <a className="btn chico" href={`#/paciente/${f.id}/ficha`} target="_blank" rel="noopener">Abrir su ficha</a>
        <button className="btn chico pri" onClick={() => archivar(otra, f)} disabled={a.ocupado} title="Esta queda como la ficha principal y la otra se archiva">Dejar esta como principal</button>
      </div>
    </div>
  );

  return (
    <div className="card" style={{ boxShadow: "none", display: "grid", gap: 10 }}>
      <h3 style={{ margin: 0 }}>Pacientes duplicados</h3>
      <p style={{ margin: 0 }}>
        Aquí aparecen las fichas que parecen ser la misma persona registrada dos veces. Elige cuál queda como <b>principal</b>: la otra se <b>archiva</b>.
        No se borra nada: la historia, las recetas, las notas firmadas y los recibos de la ficha archivada se siguen viendo desde la ficha principal («Fichas anteriores»).
        Solo las citas futuras pasan a la ficha principal. La ficha archivada deja de salir en búsquedas, agenda y mensajes. Si te equivocas, se puede volver a activar.
      </p>
      <AvisoError error={a.error} />
      {msg && <div className="aviso ok" role="status">{msg}</div>}
      {datos === null ? <p className="muted">Buscando…</p> : datos.parejas.length === 0 ? (
        <div className="aviso ok" role="status">No hay fichas duplicadas pendientes de revisar.</div>
      ) : (
        <>
          <div className="aviso info" role="status">
            {datos.total} {datos.total === 1 ? "pareja por revisar" : "parejas por revisar"}{datos.total > datos.parejas.length ? ` (se muestran las primeras ${datos.parejas.length}; al resolverlas aparecen las demás)` : ""}.
          </div>
          {datos.parejas.map((p) => (
            <div key={`${p.a.id}-${p.b.id}`} style={{ display: "grid", gap: 8, borderTop: "1px solid var(--line, #ddd)", paddingTop: 10 }}>
              <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>{lado(p.a, p.b)}{lado(p.b, p.a)}</div>
              <div><button className="btn chico" onClick={() => noSonIguales(p)} disabled={a.ocupado}>No son la misma persona</button></div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
